'use client'

import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { use, useEffect, useMemo, useRef, useState } from 'react'

import { ModelsAccountStatus } from '@/api/generated-api'
import {
  useClubOwnerProfile,
  useClubPublicProfile
} from '@/features/clubs/hooks'
import {
  athleteDisplayName,
  isAthleteProfile
} from '@/features/clubs/lib/athleteProfile'
import { ClubNotificationSettings } from '@/features/notifications/components/ClubNotificationSettings'
import { useClubMe } from '@/hooks/useClubMe'
import { useUserMe } from '@/hooks/useUserMe'
import { isAthleteEnabled } from '@/lib/auth/athleteFeature'
import { currentAccountType, type TabName, track } from '@/lib/productAnalytics'
import Loader from '@/shared/ui/Loader'
import { useAuthStore } from '@/stores/authStore'
import { getAuthTokenPayload } from '@/utils/auth'

import S from './ClubProfilePage.module.scss'
import ActivationProgressPanel from './components/ActivationProgressPanel'
import AthleteFanAction from './components/AthleteFanAction'
import BecomeFanButton from './components/BecomeFanButton'
import BlockedClubProfileView from './components/BlockedClubProfileView'
// AI Posting temporarily hidden — feature still in "Soon" (see block below).
// Restore this import together with the block when the feature ships.
// import ClubAIPosting from './components/ClubAIPosting'
import ClubFansLeaderboard from './components/ClubFansLeaderboard'
import ClubMonetization from './components/ClubMonetization'
import ClubNotFound from './components/ClubNotFound'
import ClubProfileAnalytics from './components/ClubProfileAnalytics'
import ClubProfileCard from './components/ClubProfileCard'
import ClubProfileError from './components/ClubProfileError'
import ClubProfileHero from './components/ClubProfileHero'
import ClubProfileStats from './components/ClubProfileStats'
import ClubProfileTabs, {
  ALL_CLUB_PROFILE_TABS,
  type ClubProfileTab
} from './components/ClubProfileTabs'
import StopBeingFanButton from './components/StopBeingFanButton'

const USER_ROLE_CLUB = 'USER_ROLE_CLUB'
const USER_ROLE_USER = 'USER_ROLE_USER'

// tab_name mapping (task-4-report tab-mapping table) — UI keys match 1:1
// except this file's own case; kept explicit for readability.
const CLUB_PROFILE_TAB_NAMES: Record<ClubProfileTab, TabName> = {
  overview: 'overview',
  analytics: 'analytics',
  fansLeaderboard: 'fans_leaderboard',
  monetization: 'monetization'
}

type PageParams = { clubId?: string | string[] }

interface ClubProfilePageProps {
  params: Promise<PageParams>
}

const ClubProfilePage = ({ params }: ClubProfilePageProps) => {
  const [activeTab, setActiveTab] = useState<ClubProfileTab>('overview')
  const resolved = use(params)
  const clubId = Array.isArray(resolved.clubId)
    ? resolved.clubId[0]
    : resolved.clubId
  const safeClubId = clubId || ''

  const isAuthenticated = useAuthStore(s => s.isAuthenticated)
  const searchParams = useSearchParams()
  const isPublicPreview = searchParams.get('publicPreview') === '1'
  const t = useTranslations('club.hero')
  const tNotifications = useTranslations('notifications')

  const {
    data: publicData,
    isLoading: isPublicLoading,
    isError: isPublicError,
    error: publicError,
    refetch: refetchPublic
  } = useClubPublicProfile(safeClubId)

  const {
    data: myClubData,
    isLoading: isMyClubLoading,
    refetch: refetchMyClub
  } = useClubMe({ enabled: !isPublicPreview })

  // Wait for /me to load before showing favourite-related controls — avoids a
  // flash of the button (and accidental overwrite via quick Confirm) while
  // userMe is still loading.
  const { data: userMe } = useUserMe()
  const profileLoaded = Boolean(userMe?.user)
  const favoriteClubId = userMe?.user?.favorite_club_id
  // Become a fan: profile loaded AND this club is not the favourite — with
  // another favourite (club, athlete or Requested Club) it is a switch
  // (BAT-1107, Выбор Favorite Club §9).
  const canBecomeFan = profileLoaded && favoriteClubId !== safeClubId
  // Stop being a fan: profile loaded AND this is the user's favourite club.
  const canStopBeingFan = profileLoaded && favoriteClubId === safeClubId

  // Stabilise auth: don't compute isOwner until auth data has settled.
  const authSettled =
    isPublicPreview || !isAuthenticated || (isAuthenticated && !isMyClubLoading)

  const roles = useMemo(() => getAuthTokenPayload()?.roles || [], [])
  const isClubUser = roles.includes(USER_ROLE_CLUB)
  const isUserRole = roles.includes(USER_ROLE_USER)

  // publicData is now ClubPublicProfile | null — the club record lives at
  // publicData.club, with leaderboard enrichment as sibling fields.
  const club = publicData?.club ?? null
  // BAT-1106: an athlete is the same page; while the athlete feature is off
  // its profile is not shown at all.
  const isAthlete = isAthleteProfile(club)
  const isHiddenAthlete = isAthlete && !isAthleteEnabled()
  const isOwner =
    !isPublicPreview &&
    authSettled &&
    isClubUser &&
    Boolean(myClubData?.club?.id) &&
    Boolean(club?.id) &&
    myClubData!.club!.id === club!.id

  const ownerClubStatus = isOwner ? myClubData?.club?.status : undefined

  const { data: ownerData } = useClubOwnerProfile({
    enabled: !isPublicPreview && authSettled && isOwner
  })

  // Sync status updates that happen out-of-band (e.g. admin approves application).
  // Both `useClubPublicProfile` (5m staleTime) and `useClubMe` can stay "fresh"
  // in react-query cache, so the Edit button may not reflect status changes
  // until logout/login. If owner statuses differ, force a refetch.
  useEffect(() => {
    if (isPublicPreview || !authSettled || !isOwner) return
    const publicStatus = club?.status
    const myStatus = myClubData?.club?.status
    if (!publicStatus || !myStatus) return
    if (publicStatus === myStatus) return
    refetchMyClub()
    refetchPublic()
  }, [
    authSettled,
    isOwner,
    club?.status,
    myClubData?.club?.status,
    refetchMyClub,
    refetchPublic
  ])

  const effectiveClubStatus = ownerClubStatus ?? club?.status
  const isBlockedOwner =
    isOwner && effectiveClubStatus === ModelsAccountStatus.AccountStatusBlocked

  // Other-club (fan) view shows only Overview + Funs leaderboard; the owner
  // keeps the full management set on their own club. `effectiveTab` normalises
  // the active tab so hidden-tab content never renders if `isOwner` flips.
  const visibleTabs: readonly ClubProfileTab[] = isOwner
    ? ALL_CLUB_PROFILE_TABS
    : ['overview', 'fansLeaderboard']
  const effectiveTab = visibleTabs.includes(activeTab)
    ? activeTab
    : visibleTabs[0]

  // profile_tab_viewed: fires once per shown (tab, owner type), only after the
  // page and owner status have settled, and never for the blocked-owner screen.
  const trackedClubTabRef = useRef<string | null>(null)
  useEffect(() => {
    if (
      isPublicLoading ||
      isPublicError ||
      !club ||
      !authSettled ||
      isBlockedOwner ||
      isHiddenAthlete
    ) {
      return
    }
    // US-ATH-08.1 / 13.1: an athlete's page is «athlete», own or public.
    const ownerType = isAthlete
      ? 'athlete'
      : isOwner
      ? 'club_owner'
      : 'club_public'
    const key = `${effectiveTab}|${ownerType}`
    if (trackedClubTabRef.current === key) {
      return
    }
    trackedClubTabRef.current = key
    track({
      name: 'profile_tab_viewed',
      props: {
        account_type: currentAccountType(),
        profile_owner_type: ownerType,
        tab_name: CLUB_PROFILE_TAB_NAMES[effectiveTab]
      }
    })
  }, [
    effectiveTab,
    isOwner,
    isPublicLoading,
    isPublicError,
    club,
    authSettled,
    isBlockedOwner,
    isAthlete,
    isHiddenAthlete
  ])

  if (isPublicLoading) {
    return (
      <div className={S.loading}>
        <Loader variant="fullscreen" />
      </div>
    )
  }

  if (isPublicError) {
    const status = (publicError as { status?: number })?.status
    if (status === 404) {
      return <ClubNotFound />
    }
    return <ClubProfileError onRetry={() => refetchPublic()} />
  }

  if (!publicData || !club || isHiddenAthlete) {
    return <ClubNotFound />
  }

  const clubForView =
    isOwner && ownerClubStatus ? { ...club, status: ownerClubStatus } : club

  if (isBlockedOwner) {
    return <BlockedClubProfileView club={clubForView} />
  }

  return (
    <div className={S.page}>
      <div className={S.heroArea}>
        <ClubProfileHero bannerUrl={undefined} bannerAlt={t('backgroundAlt')} />
        <div className={S.cardOverlay}>
          <ClubProfileCard
            club={clubForView}
            isOwner={isOwner}
            actionSlot={
              isAthlete ? (
                // US-ATH-13.1: guest → sign in; the backend decides who
                // may become a fan (hidden for clubs, athletes and the owner).
                !isOwner ? (
                  <AthleteFanAction
                    clubId={safeClubId}
                    name={athleteDisplayName(club)}
                  />
                ) : null
              ) : !isOwner && isAuthenticated && isUserRole ? (
                canStopBeingFan ? (
                  <StopBeingFanButton clubId={safeClubId} />
                ) : canBecomeFan ? (
                  <BecomeFanButton
                    clubId={safeClubId}
                    name={club.club_name || ''}
                  />
                ) : null
              ) : null
            }
          />
        </div>
      </div>

      <div className={S.content}>
        {/* US-ATH-07.1: the athlete's edit is on moderation — fans still see
            the current profile; «Edit profile» waits for the decision. */}
        {isOwner &&
        isAthlete &&
        effectiveClubStatus === ModelsAccountStatus.AccountStatusInEdit ? (
          <p className={S.moderationNotice} role="status">
            {t('editOnModeration')}
          </p>
        ) : null}
        {effectiveTab === 'overview' &&
          ownerData?.activation_progress &&
          ownerData.activation_progress.length > 0 &&
          // BAT-1108: an athlete's panel goes once withdrawals may open
          // (approved + fans + announcement).
          !(
            isAthlete && ownerData.activation_summary?.monetization_available
          ) && (
            <ActivationProgressPanel
              steps={ownerData.activation_progress}
              fanCount={club.fan_count}
              summary={ownerData.activation_summary}
              isAthlete={isAthlete}
            />
          )}

        <div className={S.tabsWrapper}>
          <ClubProfileTabs
            tabs={visibleTabs}
            activeTab={effectiveTab}
            onTabChange={setActiveTab}
          />
        </div>

        {/*
          AI Posting block temporarily hidden — feature still in "Soon".
          Restore this block together with the import at the top when it ships.
          <div className={S.aiPosting}>
            <ClubAIPosting />
          </div>
        */}

        {effectiveTab === 'overview' && (
          <>
            <ClubProfileStats
              club={clubForView}
              clubRank={
                isAthlete ? publicData.athlete_rank : publicData.global_rank
              }
              balancePts={publicData.balance_pts}
              totalEarnedPts={publicData.total_earned_pts}
              fanViews={publicData.fan_views}
              challengesCompleted={publicData.challenges_completed}
              onViewLeaderboard={() => setActiveTab('fansLeaderboard')}
              isAthlete={isAthlete}
            />
            {/* BAT-987 / C9: a club gets email only — no centre, no push. */}
            {isOwner ? (
              <section
                className={S.notifications}
                aria-labelledby="club-notification-settings-title"
              >
                <h2
                  id="club-notification-settings-title"
                  className={S.notificationsTitle}
                >
                  {tNotifications('settings.title')}
                </h2>
                <ClubNotificationSettings email={userMe?.user?.email} />
              </section>
            ) : null}
          </>
        )}

        {effectiveTab === 'analytics' && isOwner ? (
          <ClubProfileAnalytics
            fanViews={publicData.fan_views}
            fanCount={club.fan_count}
            tasksCompleted={publicData.challenges_completed}
            earnedAp={publicData.total_earned_pts ?? publicData.balance_pts}
            isAthlete={isAthlete}
          />
        ) : null}

        {effectiveTab === 'monetization' && isOwner ? (
          <ClubMonetization
            canWithdraw={
              ownerData?.activation_summary?.monetization_available === true
            }
          />
        ) : null}

        {effectiveTab === 'fansLeaderboard' && (
          <ClubFansLeaderboard
            clubId={safeClubId}
            clubName={isAthlete ? athleteDisplayName(club) : club?.club_name}
          />
        )}
      </div>
    </div>
  )
}

export default ClubProfilePage
